import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import '../styles/AppointmentBooking.css';

const AppointmentBooking = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    reason: ''
  });

  const [errors, setErrors] = useState({});

  const timeSlots = [
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time slot is required';
    if (!formData.reason.trim()) newErrors.reason = 'Reason for visit is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Mock submission
      toast({
        title: "Appointment Request Submitted",
        description: "We'll contact you within 24 hours to confirm your appointment.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        reason: ''
      });
      setErrors({});
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section id="booking" className="appointment-booking-section">
      <div className="booking-container">
        <div className="booking-content">
          <div className="booking-info">
            <div className="section-label">Book Consultation</div>
            <h2 className="section-title">Schedule Your Appointment</h2>
            <p className="booking-description">
              Take the first step towards better heart health. Our team will contact you within 24 hours to confirm your preferred appointment time.
            </p>
            
            <div className="booking-features">
              <div className="booking-feature">
                <div className="feature-icon"><Calendar size={24} /></div>
                <div>
                  <h4>Flexible Scheduling</h4>
                  <p>Morning and evening slots available</p>
                </div>
              </div>
              <div className="booking-feature">
                <div className="feature-icon"><Clock size={24} /></div>
                <div>
                  <h4>Extended Consultations</h4>
                  <p>Unhurried, comprehensive sessions</p>
                </div>
              </div>
              <div className="booking-feature">
                <div className="feature-icon"><MessageSquare size={24} /></div>
                <div>
                  <h4>Online & In-Person</h4>
                  <p>Choose your preferred consultation mode</p>
                </div>
              </div>
            </div>
            
            <div className="booking-privacy">
              <p>🔒 Your information is secure and confidential. We adhere to strict medical privacy standards.</p>
            </div>
          </div>
          
          <div className="booking-form-wrapper">
            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <User size={18} />
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <Mail size={18} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <Phone size={18} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                    placeholder="+91 98765 43210"
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date" className="form-label">
                    <Calendar size={18} />
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={`form-input ${errors.date ? 'error' : ''}`}
                  />
                  {errors.date && <span className="error-message">{errors.date}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="time" className="form-label">
                    <Clock size={18} />
                    Preferred Time *
                  </label>
                  <select
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className={`form-input ${errors.time ? 'error' : ''}`}
                  >
                    <option value="">Select a time slot</option>
                    {timeSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                  {errors.time && <span className="error-message">{errors.time}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="reason" className="form-label">
                  <MessageSquare size={18} />
                  Reason for Visit *
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows="4"
                  className={`form-input form-textarea ${errors.reason ? 'error' : ''}`}
                  placeholder="Please describe your symptoms or reason for consultation..."
                />
                {errors.reason && <span className="error-message">{errors.reason}</span>}
              </div>
              
              <button type="submit" className="submit-button">
                <Send size={20} />
                Submit Appointment Request
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentBooking;
